import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit, output } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { KnowledgeSourceConfigType, TrainingProgress, WebsiteScrapeConfigProgress } from '@vendasta/embeddings';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AiKnowledgeService, AiLoadingSpinnerComponent, BUSINESS_PROFILE_URL_TOKEN } from '@galaxy/ai-knowledge';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { KnowledgeForm } from '../../../../core/forms';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { ACCOUNT_GROUP_ID_TOKEN } from '../../../../core/tokens';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { modeOptions } from '../knowledge-card-constants';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { KnowledgeWebPagesDialogComponent } from './knowledge-web-pages-dialog/knowledge-web-pages-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'ai-assistant-knowledge-access-form',
  imports: [
    MatCardModule,
    MatExpansionModule,
    NgTemplateOutlet,
    TranslateModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatSelect,
    MatOption,
    MatIconButton,
    MatIcon,
    DatePipe,
    GalaxyEmptyStateModule,
    AiLoadingSpinnerComponent,
  ],
  templateUrl: './assistant-knowledge-access-form.component.html',
  styleUrl: './assistant-knowledge-access-form.component.scss',
})
export class AssistantKnowledgeAccessFormComponent implements OnInit {
  form = input.required<KnowledgeForm>();
  remove = output();

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly accountGroupService = inject(AccountGroupService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly dialog = inject(MatDialog);

  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly accountGroupId = toSignal(this.accountGroupId$.pipe(distinctUntilChanged()));

  protected readonly accountGroupNameResource = rxResource({
    request: () => ({ accountID: this.accountGroupId() || '' }),
    loader: ({ request }) => {
      return this.accountGroupService
        .get(request.accountID, new ProjectionFilter({ napData: true }))
        .pipe(map((accountGroup) => accountGroup.napData.companyName));
    },
  });

  protected readonly businessProfileUrl = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  knowledgeSource = linkedSignal(() => this.form().controls.raw.getRawValue());
  knowledgeName = linkedSignal<string>(() => this.knowledgeSource().name);
  type = computed(() => {
    const knowledgeSource = this.knowledgeSource();
    switch (true) {
      case !!knowledgeSource.config.websiteScrapeConfig:
        return KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE;
      case !!knowledgeSource.config.customDataConfig:
        return KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA;
      case !!knowledgeSource.config.businessProfileConfig:
        return KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_BUSINESS_PROFILE;
      default:
        return KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_INVALID;
    }
  });
  lastRefreshed = computed(() => this.knowledgeSource().config.websiteScrapeConfig.lastScraped);
  panelDescription = linkedSignal(() => this.knowledgeService.getKnowledgePreview(this.knowledgeSource()));
  protected scrapingWebsite = linkedSignal(
    () =>
      this.type() === KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE &&
      this.knowledgeSource().config.websiteScrapeConfig.scrapeProgress !==
        WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE,
  );
  protected trainingAI = linkedSignal(
    () =>
      this.type() === KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE &&
      this.knowledgeSource().training.progress !== TrainingProgress.TRAINING_DONE,
  );
  protected refreshingKnowledge = linkedSignal(() => {
    const knowledgeSource = this.knowledgeSource();
    return (
      (this.type() === KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE &&
        knowledgeSource.config.websiteScrapeConfig.scrapeProgress !==
          WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE) ||
      knowledgeSource.training.progress !== TrainingProgress.TRAINING_DONE
    );
  });
  protected refreshingTitle = computed(() => {
    const scraping = this.scrapingWebsite();
    const training = this.trainingAI();
    if (scraping) {
      return this.translateService.instant('AI_ASSISTANT.KNOWLEDGE_ACCESS.FINDING_PAGES');
    } else if (training) {
      return this.translateService.instant('AI_ASSISTANT.KNOWLEDGE_ACCESS.TRAINING');
    }
  });

  ngOnInit() {
    this.form()
      .get('name')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newName) => {
        this.knowledgeName.set(newName);
      });

    if (this.type() === KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE) {
      this.form()
        .get('url')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newUrl) => {
          this.panelDescription.set(newUrl);
        });
    } else if (this.type() === KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA) {
      this.form()
        .get('content')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((content) => {
          this.panelDescription.set(content);
        });
    }
  }

  constructor() {
    // refresh the knowledge source every 5 seconds
    effect(() => {
      if (!this.refreshingKnowledge()) return;

      const refresh = async () => {
        const ks = await this.knowledgeService.getKnowledgeSource(
          this.knowledgeSource().id,
          this.knowledgeSource().namespace,
        );

        if (
          ks.training.progress === TrainingProgress.TRAINING_DONE &&
          ks.config.websiteScrapeConfig.scrapeProgress ===
            WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE
        ) {
          this.knowledgeSource.set(ks);
        } else {
          this.trainingAI.set(ks.training.progress !== TrainingProgress.TRAINING_DONE);
          this.scrapingWebsite.set(
            ks.config.websiteScrapeConfig.scrapeProgress !==
              WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE,
          );
          setTimeout(refresh, 5000);
        }
      };

      refresh();
    });
  }

  protected async openKnowledgeWebPagesDialog(): Promise<void> {
    const res = await firstValueFrom(
      this.dialog
        .open(KnowledgeWebPagesDialogComponent, {
          width: '500px',
          data: { knowledgeSource: this.knowledgeSource() },
        })
        .afterClosed(),
    );

    if (res?.refresh) {
      this.refreshingKnowledge.set(true);
    }
  }

  protected async rescrape(): Promise<void> {
    this.confirmationModal
      .openModal({
        title: this.translateService.instant('AI_ASSISTANT.KNOWLEDGE_ACCESS.REFRESH_CONFIRMATION_TITLE'),
        message: this.translateService.instant('AI_ASSISTANT.KNOWLEDGE_ACCESS.REFRESH_CONFIRMATION_MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe(async (confirmed) => {
        if (confirmed) {
          const knowledgeSource = this.knowledgeSource();
          await this.knowledgeService.rescrapeWebsite(knowledgeSource);
          await this.knowledgeService.getKnowledgeSource(knowledgeSource.id, knowledgeSource.namespace).then((ks) => {
            this.knowledgeSource.set(ks);
          });
        }
      });
  }

  protected readonly KnowledgeSourceConfigType = KnowledgeSourceConfigType;
  protected readonly TrainingProgress = TrainingProgress;
  protected readonly modeOptions = modeOptions;
}
